// Cumulative state of the agent learning loop (GET /stats) plus the
// "Run learning pass now" trigger (POST /run). Rendered above the
// Feedback lessons and Eval cases tabs of the Agent Eval page.

import React, { useCallback, useEffect, useState } from 'react';

import { feedbackLearningApi, type LoopStats } from '../../services/feedbackLearning';
import styles from '../AgentEvalPage.module.css';
import { errorMessage, formatUsd } from './format';

function countChips(counts: Record<string, number>): React.ReactElement {
  const keys = Object.keys(counts).sort();
  if (keys.length === 0) return <span className={styles.score}>—</span>;
  return (
    <>
      {keys.map((k) => (
        <span key={k} className={`${styles.tag} ${styles.pending}`} data-testid={`stat-${k}`}>
          {k} {counts[k]}
        </span>
      ))}
    </>
  );
}

export function LearningLoopStats({
  refreshKey,
}: {
  /** Bump to reload the numbers (after a curation or a queued pass). */
  refreshKey: number;
}): React.ReactElement {
  const [stats, setStats] = useState<LoopStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const [queued, setQueued] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setStats(await feedbackLearningApi.stats());
    } catch (e) {
      setError(errorMessage(e, 'failed to load learning-loop stats'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const runNow = useCallback(async () => {
    setRunning(true);
    setError(null);
    setQueued(null);
    try {
      const r = await feedbackLearningApi.runNow();
      setQueued(r.task_id);
    } catch (e) {
      setError(errorMessage(e, 'failed to queue a learning pass'));
    } finally {
      setRunning(false);
    }
  }, []);

  return (
    <div className={styles['loop-stats']} aria-label="Learning loop stats" data-section="loop_stats">
      <div className={styles['loop-stats-grid']}>
        <div>
          <div className={styles['stat-label']}>Ledger by status</div>
          <div className={styles['stat-chips']}>{countChips(stats?.by_status ?? {})}</div>
        </div>
        <div>
          <div className={styles['stat-label']}>By source</div>
          <div className={styles['stat-chips']}>{countChips(stats?.by_source ?? {})}</div>
        </div>
        <div>
          <div className={styles['stat-label']}>Eval cases</div>
          <div className={styles['stat-chips']}>{countChips(stats?.eval_cases_by_status ?? {})}</div>
        </div>
        <div>
          <div className={styles['stat-label']}>KB writes</div>
          <div className={styles['stat-value']} data-testid="stat-kb-writes">
            {stats ? stats.kb_writes : '—'}
          </div>
        </div>
        <div>
          <div className={styles['stat-label']}>Questions asked</div>
          <div className={styles['stat-value']} data-testid="stat-questions">
            {stats ? stats.questions : '—'}
          </div>
        </div>
        <div>
          <div className={styles['stat-label']}>Cumulative cost</div>
          <div className={styles['stat-value']} data-testid="stat-cost">
            {stats ? formatUsd(stats.total_cost_usd) : '—'}
          </div>
        </div>
      </div>
      <div className={styles['loop-stats-actions']}>
        <button
          type="button"
          className={styles['run-now']}
          disabled={running}
          onClick={() => void runNow()}
          data-action="agent_eval_learning_run_now"
          data-outcome="request"
        >
          {running ? 'Queuing…' : 'Run learning pass now'}
        </button>
        {queued && (
          <span className={styles['run-queued']} data-testid="run-queued">
            Pass queued (task {queued}). Reload in a minute to see new rows.
          </span>
        )}
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
